import React, { useState, useEffect, useRef } from 'react';
import { getCountryCallingCode, getCountries } from 'libphonenumber-js/max';
import { FaChevronDown } from "react-icons/fa6";

const SeparatePhoneInput = ({ 
  mobileCodeValue, 
  mobileNumberValue, 
  onMobileCodeChange, 
  onMobileNumberChange, 
  onMobileNumberBlur,
  mobileCodeError, 
  mobileNumberError, 
  className = '' 
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const getAllCountries = () => {
    const supportedCountries = getCountries();
    return supportedCountries.map(countryCode => {
      try {
        const callingCode = getCountryCallingCode(countryCode);
        return {
          code: countryCode,
          name: getCountryName(countryCode),
          flag: getCountryFlag(countryCode),
          callingCode: `+${callingCode}`
        };
      } catch {
        return null;
      }
    }).filter(Boolean);
  };
  const getCountryName = (countryCode) => {
    try {
      const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
      return displayNames.of(countryCode) || countryCode;
    } catch {
      return countryCode;
    }
  };
  const getCountryFlag = (countryCode) => {
    return countryCode
      .toUpperCase()
      .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt()));
  };

  const validCountries = getAllCountries();
  const filteredCountries = validCountries.filter(country => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(searchLower) ||
      country.code.toLowerCase().includes(searchLower) ||
      country.callingCode.includes(searchTerm)
    );
  });
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 dark:bg-yellow-600 text-gray-900 dark:text-white">
          {part}
        </span>
      ) : part
    );
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);
  useEffect(() => {
    if (mobileCodeValue && validCountries.length > 0) {
      const country = validCountries.find(c => c.callingCode === mobileCodeValue);
      if (country) {
        setSelectedCountry(country.code);
      }
    }
  }, [mobileCodeValue, validCountries]);
  useEffect(() => {
    if (!selectedCountry && validCountries.length > 0 && !mobileCodeValue) {
      const indiaCountry = validCountries.find(country => country.code === 'IN');
      if (indiaCountry) {
        setSelectedCountry(indiaCountry.code);
        onMobileCodeChange(indiaCountry.callingCode);
      } else {
        const defaultCountry = validCountries[0];
        setSelectedCountry(defaultCountry.code);
        onMobileCodeChange(defaultCountry.callingCode);
      }
    }
  }, [validCountries, selectedCountry, mobileCodeValue, onMobileCodeChange]);

  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    const selectedCountryData = validCountries.find(c => c.code === countryCode);
    if (selectedCountryData) {
      onMobileCodeChange(selectedCountryData.callingCode);
    }
    setIsDropdownOpen(false);
    setSearchTerm(''); 
  };
  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setSearchTerm(''); 
    if (!isDropdownOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleNumberChange = (e) => {
    const inputValue = e.target.value;
    const number = inputValue.replace(/\D/g, '');
    onMobileNumberChange(number);
  };

  const getCallingCode = (countryCode) => {
    const country = validCountries.find(c => c.code === countryCode);
    return country ? country.callingCode : '';
  };

  const selectedCountryData = validCountries.find(c => c.code === selectedCountry);

  return (
    <div className={`flex gap-3 ${className}`}>
      {/* Mobile Code Field - Very Small width */}
      <div className="w-28" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Mobile Code <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          {/* Custom Dropdown Button */}
          <button
            type="button"
            onClick={handleDropdownToggle}
            className={`w-full pl-3 py-2 pr-8 rounded-lg border ${
              mobileCodeError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-primary"
            } bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-all duration-200 outline-none cursor-pointer text-sm font-medium text-left`}
          >
            <span>
              {selectedCountryData?.flag} {getCallingCode(selectedCountry) || "Select"}
            </span>
          </button>

          {/* Dropdown Arrow */}
          <div className="absolute inset-y-0 right-2.5 flex items-center pointer-events-none">
            <FaChevronDown
              className={`w-3 h-3 text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
            />
          </div>

          {/* Custom Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-[9999] min-w-64">
              {/* Search Input */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search countries..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                {searchTerm && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {filteredCountries.length} countries found
                  </div>
                )}
              </div>
              
              {/* Countries List */}
              <div className="max-h-48 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountryChange(country.code)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-600 text-xs font-medium text-gray-900 dark:text-white transition-colors whitespace-nowrap border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                    >
                      {country.flag} {country.callingCode} {highlightMatch(country.name, searchTerm)}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {mobileCodeError && <p className="mt-1 text-xs text-red-500">{mobileCodeError}</p>}
      </div>

      {/* Mobile Number Field - Flexible width */}
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Mobile Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          value={mobileNumberValue || ""}
          onChange={handleNumberChange}
          onBlur={(e) => onMobileNumberBlur && onMobileNumberBlur(e.target.value)}
          placeholder="Enter Mobile Number"
          className={`w-full px-4 py-2 rounded-lg border ${
            mobileNumberError
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 dark:border-gray-600 focus:ring-primary"
          } bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-all duration-200 outline-none placeholder-gray-400 dark:placeholder-gray-500 text-sm`}
        />
        {mobileNumberError && <p className="mt-1 text-sm text-red-500">{mobileNumberError}</p>}
      </div>
    </div>
  );
};

export default SeparatePhoneInput;